import React, { useEffect, useState } from 'react';
import baseURL from '../baseURL';
import GirviPageShell from '../components/common/GirviPageShell';

const Daybook = () => {
  const [rows, setRows] = useState([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [openingBalance, setOpeningBalance] = useState(0);
  const [totalCredit, setTotalCredit] = useState(0);
  const [totalDebit, setTotalDebit] = useState(0);
  const [user, setUser] = useState(null);

  // Helper to format date to DD/MM/YYYY
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Fetch user profile on mount
  useEffect(() => {
    const fetchUser = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const profileRes = await fetch(baseURL + '/auth/profile', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const profile = await profileRes.json();
        setUser(profile.data?.user || null);
      } catch {
        setUser(null);
      }
    };
    fetchUser();
  }, []);

  // Fetch daybook from backend
  const fetchDaybook = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (from) params.append('from', from);
      if (to) params.append('to', to);
      const token = localStorage.getItem('token');
      const endpoint = `${baseURL}/accounting-entries/daybook`;

      const res = await fetch(`${endpoint}?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      
      const data = await res.json();

      const filteredRows = (data.rows || []).filter((row) => {
        const details = (row.details || '').toLowerCase();
        if (details.includes('capital')) return false;
        if (row.document === 'receipt' && row.debit > 0) return false;
        return true;
      });

      const filteredTotalCredit = filteredRows.reduce((sum, r) => sum + (r.credit || 0), 0);
      const filteredTotalDebit = filteredRows.reduce((sum, r) => sum + (r.debit || 0), 0);

      setRows(filteredRows);
      setOpeningBalance(data.openingBalance || 0);
      setTotalCredit(filteredTotalCredit);
      setTotalDebit(filteredTotalDebit);
    } catch (err) {
      setError('Failed to fetch daybook.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Optionally, set default dates to today
    const today = new Date().toISOString().slice(0, 10);
    setFrom(today);
    setTo(today);
  }, []);

  useEffect(() => {
    if (from && to) fetchDaybook();
    // eslint-disable-next-line
  }, [from, to]);

  return (
    <GirviPageShell
      title="Daybook"
      subtitle="Transactions · Registers"
      chipLabel="Transactions"
      maxWidth="xl"
     compact>
      {user && (
        <div className="girvi-letterhead">
          <h2 style={{ margin: 0 }}>{user.name}</h2>
          <div>{user.companyName}</div>
          <div>{user.address}</div>
          <div>Mobile: {user.mobileNumber}</div>
          <div>License: {user.licenseNumber}</div>
        </div>
      )}
      <div className="girvi-filterbar">
        <label>From <input type="date" value={from} onChange={e => setFrom(e.target.value)} /></label>
        <label>To <input type="date" value={to} onChange={e => setTo(e.target.value)} /></label>
        <button onClick={fetchDaybook} disabled={loading}>Filter</button>
      </div>
      <div className="girvi-stats">
        <span><strong>Opening Balance:</strong> ₹{openingBalance.toFixed(2)}</span>
        <span><strong>Total Credit:</strong> ₹{totalCredit.toFixed(2)}</span>
        <span><strong>Total Debit:</strong> ₹{totalDebit.toFixed(2)}</span>
        <span><strong>Closing Balance:</strong> ₹{(openingBalance + totalCredit - totalDebit).toFixed(2)}</span>
      </div>
      {loading ? <p>Loading...</p> : error ? <p style={{ color: 'red' }}>{error}</p> : (
        <table border="1" cellPadding="6" style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
          <thead style={{ background: '#f0f0f0' }}>
            <tr>
              <th>Date</th>
              <th>Document</th>
              <th>Account Code</th>
              <th>Account Name</th>
              <th>Cheque Number</th>
              <th>Details</th>
              <th>Credit</th>
              <th>Debit</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={8} style={{ textAlign: 'center' }}>No entries found.</td></tr>
            )}
            {rows.map((row, idx) => (
              <tr key={idx}>
                <td>{formatDate(row.date)}</td>
                <td>{row.document}</td>
                <td>{row.accountCode}</td>
                <td>{row.accountName}</td>
                <td>{row.chequeNumber}</td>
                <td>{row.details}</td>
                <td style={{ textAlign: 'right' }}>{row.credit ? row.credit.toFixed(2) : ''}</td>
                <td style={{ textAlign: 'right' }}>{row.debit ? row.debit.toFixed(2) : ''}</td>
              </tr>
            ))}
          </tbody>
          <tfoot style={{ background: '#f9f9f9', fontWeight: 'bold' }}>
            <tr>
              <td colSpan={6} style={{ textAlign: 'right' }}>TOTAL</td>
              <td style={{ textAlign: 'right' }}>{totalCredit.toFixed(2)}</td>
              <td style={{ textAlign: 'right' }}>{totalDebit.toFixed(2)}</td>
            </tr>
          </tfoot>
        </table>
      )}
    </GirviPageShell>
  );
};

export default Daybook; 